import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Users, ShieldCheck, LinkSimple, Lock, LockOpen, TrendUp, Warning, Copy, SignOut, UserPlus, Trash, Eye, EyeSlash, WhatsappLogo, Key, Crown, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LoadingBar from '../components/LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Função para copiar texto com fallback
const copyToClipboard = async (text) => {
  try {
    // Tenta usar a Clipboard API moderna
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // Fallback silencioso
  }
  
  // Fallback usando execCommand (funciona em mais contextos)
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    console.error('Fallback copy failed:', err);
    return false;
  }
};

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [churnMetrics, setChurnMetrics] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [siteConfig, setSiteConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordModalAction, setPasswordModalAction] = useState(null); // 'view' | 'edit'
  const [adminPassword, setAdminPassword] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [sensitiveToken, setSensitiveToken] = useState(null);
  const [fullKeys, setFullKeys] = useState(null);
  const [editingKeys, setEditingKeys] = useState(null);
  const [savingKeys, setSavingKeys] = useState(false);

  // Email management
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailCounts, setEmailCounts] = useState({});
  const [emailStatusFilter, setEmailStatusFilter] = useState('all');
  const [emailTestTo, setEmailTestTo] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const fetchEmailLogs = async () => {
    try {
      const params = emailStatusFilter !== 'all' ? `?status=${emailStatusFilter}` : '';
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/emails${params}`, { withCredentials: true });
      setEmailLogs(data.logs || []);
      setEmailCounts(data.counts || {});
    } catch (err) {
      toast.error('Erro ao carregar logs de e-mail');
    }
  };

  useEffect(() => {
    if (activeTab !== 'emails') return;
    let cancelled = false;
    (async () => {
      try {
        const params = emailStatusFilter !== 'all' ? `?status=${emailStatusFilter}` : '';
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/emails${params}`, { withCredentials: true });
        if (cancelled) return;
        setEmailLogs(data.logs || []);
        setEmailCounts(data.counts || {});
      } catch (err) {
        if (!cancelled) toast.error('Erro ao carregar logs de e-mail');
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, emailStatusFilter]);

  const handleSendTestEmail = async () => {
    if (!emailTestTo.trim()) { toast.error('Informe um e-mail'); return; }
    setSendingTestEmail(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/emails/test`, { to: emailTestTo.trim() }, { withCredentials: true });
      toast.success(`E-mail de teste enviado para ${emailTestTo}`);
      setEmailTestTo('');
      setTimeout(fetchEmailLogs, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao enviar');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleResendEmail = async (logId, tag) => {
    if (!['welcome', 'password_reset'].includes(tag)) {
      toast.error(`Reenvio automático não suportado para "${tag}"`);
      return;
    }
    if (!window.confirm('Isto irá gerar uma nova senha temporária para o usuário. Continuar?')) return;
    setResendingId(logId);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/emails/${logId}/resend`, {}, { withCredentials: true });
      toast.success('E-mail reenviado');
      setTimeout(fetchEmailLogs, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha no reenvio');
    } finally {
      setResendingId(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const filterParam = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [usersRes, statsRes, webhookRes, churnRes, configRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/users${filterParam}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/webhook-config`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/churn-metrics`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/site-config`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setWebhookConfig(webhookRes.data);
      if (churnRes.data) setChurnMetrics(churnRes.data);
      if (configRes.data) setSiteConfig(configRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores.');
        navigate('/dashboard');
      } else {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBlock = async (userId) => {
    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/admin/users/${userId}/block`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email) {
      toast.error('Nome e email são obrigatórios');
      return;
    }

    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users`,
        newUser,
        { withCredentials: true }
      );
      setCreatedUser(res.data);
      setShowPassword(true); // Mostrar senha automaticamente ao criar
      toast.success('Usuário criado com sucesso!');
      setNewUser({ name: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteStep(1);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    
    // Step 2 - efetuar a exclusão
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/users/${userToDelete.user_id}`,
        { withCredentials: true }
      );
      toast.success('Usuário excluído com sucesso');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setDeleteStep(1);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handleUpdateSubscription = async () => {
    if (!selectedUser) return;
    
    try {
      // Update email if changed
      if (selectedUser._editedEmail && selectedUser._editedEmail !== selectedUser._originalEmail) {
        await axios.patch(
          `${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/email`,
          { email: selectedUser._editedEmail },
          { withCredentials: true }
        );
      }
      
      await axios.patch(
        `${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/subscription`,
        {
          plan_type: selectedUser.plan_type,
          subscription_status: selectedUser.subscription_status,
          expiration_date: selectedUser.expiration_date
        },
        { withCredentials: true }
      );
      toast.success('Dados atualizados com sucesso');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar dados');
    }
  };

  const handleCopyWebhook = async () => {
    const success = await copyToClipboard(webhookConfig?.webhook_url);
    if (success) {
      toast.success('URL copiada!');
    } else {
      toast.error('Não foi possível copiar. Selecione e copie manualmente.');
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/site-config`, {
        meta_pixel_id: siteConfig?.meta_pixel_id || '',
        google_tag_id: siteConfig?.google_tag_id || ''
      }, { withCredentials: true });
      toast.success('Configurações salvas!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleCopyText = async (text) => {
    const success = await copyToClipboard(text);
    if (success) toast.success('Copiado!');
  };

  const requestPasswordVerification = (action) => {
    setPasswordModalAction(action);
    setAdminPassword('');
    setShowPasswordModal(true);
  };

  const handleVerifyPassword = async () => {
    setVerifyingPassword(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/verify-password`, { password: adminPassword }, { withCredentials: true });
      const token = res.data.sensitive_token;
      setSensitiveToken(token);
      setShowPasswordModal(false);
      setAdminPassword('');
      
      if (passwordModalAction === 'view') {
        const keysRes = await axios.post(`${BACKEND_URL}/api/admin/site-config/keys`, { sensitive_token: token }, { withCredentials: true });
        setFullKeys(keysRes.data);
        setEditingKeys(null);
      } else if (passwordModalAction === 'edit') {
        const keysRes = await axios.post(`${BACKEND_URL}/api/admin/site-config/keys`, { sensitive_token: token }, { withCredentials: true });
        setEditingKeys({ ...keysRes.data });
        setFullKeys(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Senha incorreta');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleSaveKeys = async () => {
    if (!sensitiveToken || !editingKeys) return;
    setSavingKeys(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/site-config/keys`, { ...editingKeys, sensitive_token: sensitiveToken }, { withCredentials: true });
      toast.success('Chaves de API atualizadas! Reinicie o servidor para aplicar as mudanças.');
      setEditingKeys(null);
      setSensitiveToken(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar chaves');
    } finally {
      setSavingKeys(false);
    }
  };

  const handleCloseKeysView = () => {
    setFullKeys(null);
    setEditingKeys(null);
    setSensitiveToken(null);
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/reset-password`,
        {},
        { withCredentials: true }
      );
      setResetPasswordResult(res.data);
      setResetUserId(userId);
      setResetDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  const handleUnlinkWhatsapp = async (userId, userName) => {
    if (!window.confirm(`Desvincular WhatsApp de ${userName}?`)) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/unlink-whatsapp`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desvincular WhatsApp');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Ativo</Badge>;
      case 'trial':
        return <Badge className="bg-blue-100 text-blue-700">Trial</Badge>;
      case 'lifetime':
        return <Badge className="bg-purple-100 text-purple-700">Vitalício</Badge>;
      case 'overdue':
        return <Badge className="bg-yellow-100 text-yellow-700">Inadimplente</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700">Cancelado</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  const getPlanBadge = (plan) => {
    switch (plan) {
      case 'unico':
        return <Badge className="bg-primary/10 text-primary">Plano Aretê</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  if (loading) {
    return <LoadingBar text="Carregando painel..." />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-arete.png" 
                alt="Aretê" 
                className="h-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold">Painel Administrativo</h1>
                <p className="text-xs text-muted-foreground">Gestão de Assinaturas</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              <SignOut size={18} className="mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
                </div>
                <Users size={32} className="text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:border-green-300 transition-colors" onClick={() => setStatusFilter(statusFilter === 'active' ? 'all' : 'active')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ativos</p>
                  <p className="text-3xl font-bold text-green-600">{stats?.active_users || 0}</p>
                </div>
                <TrendUp size={32} className="text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:border-yellow-300 transition-colors" onClick={() => setStatusFilter(statusFilter === 'overdue' ? 'all' : 'overdue')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Inadimplentes</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats?.overdue_users || 0}</p>
                </div>
                <Warning size={32} className="text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-purple-300 transition-colors" onClick={() => setStatusFilter(statusFilter === 'lifetime' ? 'all' : 'lifetime')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Vitalícios</p>
                  <p className="text-3xl font-bold text-purple-600">{stats?.lifetime_users || 0}</p>
                </div>
                <Crown size={32} className="text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Bloqueados</p>
                  <p className="text-3xl font-bold text-red-600">{stats?.blocked_users || 0}</p>
                </div>
                <Lock size={32} className="text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plans Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Plano</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <div className="text-center p-6 bg-primary/5 rounded-lg border border-primary/20 w-full max-w-xs">
                <p className="text-sm text-muted-foreground">Plano Aretê</p>
                <p className="text-3xl font-bold text-primary">{stats?.plans?.unico || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">assinantes ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab('users')}
            data-testid="admin-tab-users"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'users' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Usuários
          </button>
          <button
            onClick={() => setActiveTab('churn')}
            data-testid="admin-tab-churn"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'churn' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Métricas de Churn
          </button>
          <button
            onClick={() => setActiveTab('config')}
            data-testid="admin-tab-config"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'config' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Configurações
          </button>
          <button
            onClick={() => setActiveTab('emails')}
            data-testid="admin-tab-emails"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'emails' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            E-mails
          </button>
        </div>

        {/* Churn Metrics Dashboard */}
        {activeTab === 'churn' && churnMetrics && (
          <div className="space-y-4" data-testid="churn-dashboard">
            {/* Churn KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{churnMetrics.retention_rate}%</p>
                  <p className="text-xs text-muted-foreground mt-1">Taxa de Retenção</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-500">{churnMetrics.churn_rate}%</p>
                  <p className="text-xs text-muted-foreground mt-1">Taxa de Churn</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">R$ {churnMetrics.mrr?.toFixed(2).replace('.', ',')}</p>
                  <p className="text-xs text-muted-foreground mt-1">MRR Estimado</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-orange-500">{churnMetrics.churned_users}</p>
                  <p className="text-xs text-muted-foreground mt-1">Cancelados / Inadimplentes</p>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Churn Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Novos vs. Churn (últimos 6 meses)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {churnMetrics.monthly_churn?.map((m, i) => {
                    const maxVal = Math.max(...churnMetrics.monthly_churn.map(x => Math.max(x.new_users, x.churned)), 1);
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-16 shrink-0">{m.month}</span>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="h-4 bg-green-400 rounded-sm transition-all" style={{ width: `${Math.max((m.new_users / maxVal) * 100, 2)}%` }} />
                            <span className="text-xs text-green-700">+{m.new_users} novos</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="h-4 bg-red-400 rounded-sm transition-all" style={{ width: `${Math.max((m.churned / maxVal) * 100, 2)}%` }} />
                            <span className="text-xs text-red-700">-{m.churned} churn</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Métodos de Pagamento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg bg-teal-50 border border-teal-200 text-center">
                    <p className="text-2xl font-bold text-teal-700">{churnMetrics.payment_methods?.pix || 0}</p>
                    <p className="text-xs text-teal-600">PIX</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                    <p className="text-2xl font-bold text-blue-700">{churnMetrics.payment_methods?.card || 0}</p>
                    <p className="text-xs text-blue-600">Cartão</p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-center">
                    <p className="text-2xl font-bold text-purple-700">{churnMetrics.payment_methods?.lifetime || 0}</p>
                    <p className="text-xs text-purple-600">Vitalício</p>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-center">
                    <p className="text-2xl font-bold text-gray-700">{churnMetrics.payment_methods?.other || 0}</p>
                    <p className="text-xs text-gray-600">Outros</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Table */}
        {activeTab === 'users' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle>Gestão de Usuários</CardTitle>
              <CardDescription>
                {statusFilter !== 'all' && (
                  <span className="inline-flex items-center gap-1">
                    <Funnel size={14} /> Filtro: <strong>{statusFilter === 'active' ? 'Ativos' : statusFilter === 'overdue' ? 'Inadimplentes' : statusFilter === 'lifetime' ? 'Vitalícios' : statusFilter === 'cancelled' ? 'Cancelados' : 'Todos'}</strong>
                    <button onClick={() => setStatusFilter('all')} className="ml-2 text-xs text-primary underline">Limpar</button>
                  </span>
                )}
                {statusFilter === 'all' && 'Gerencie assinaturas e acesso dos usuários'}
              </CardDescription>
            </div>
            <Dialog open={createDialogOpen} onOpenChange={(open) => {
              setCreateDialogOpen(open);
              if (!open) {
                setCreatedUser(null);
                setShowPassword(false);
              }
            }}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <UserPlus size={18} />
                  Adicionar Usuário
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Adicionar Novo Usuário</DialogTitle>
                  <DialogDescription>Preencha os dados para criar um novo usuário</DialogDescription>
                </DialogHeader>
                
                {createdUser ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 font-medium mb-3">✅ Usuário criado com sucesso!</p>
                      <div className="space-y-3 text-sm">
                        <div>
                          <Label className="text-xs text-muted-foreground">Email</Label>
                          <Input 
                            value={createdUser.email} 
                            readOnly 
                            className="bg-white mt-1"
                            onClick={(e) => e.target.select()}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Senha Temporária</Label>
                          <div className="flex gap-2 mt-1">
                            <Input 
                              type={showPassword ? 'text' : 'password'}
                              value={createdUser.temp_password} 
                              readOnly 
                              className="bg-white font-mono"
                              onClick={(e) => e.target.select()}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={async () => {
                                const success = await copyToClipboard(createdUser.temp_password);
                                if (success) {
                                  toast.success('Senha copiada!');
                                } else {
                                  toast.error('Não foi possível copiar. Selecione e copie manualmente.');
                                }
                              }}
                            >
                              <Copy size={16} className="mr-1" /> Copiar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      ⚠️ Anote a senha temporária! O usuário deverá alterá-la no primeiro login.
                    </p>
                    <Button onClick={() => {
                      setCreateDialogOpen(false);
                      setCreatedUser(null);
                      setShowPassword(false);
                    }} className="w-full">
                      Fechar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-name">Nome</Label>
                      <Input 
                        id="new-name"
                        placeholder="Nome completo"
                        value={newUser.name}
                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-email">Email</Label>
                      <Input 
                        id="new-email"
                        type="email"
                        placeholder="email@exemplo.com"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      />
                    </div>
                    <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <p className="text-sm text-muted-foreground">Plano</p>
                      <p className="font-medium">Plano Aretê - R$ 29,99/mês</p>
                    </div>
                    <Button onClick={handleCreateUser} className="w-full">
                      Criar Usuário
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users size={48} className="mx-auto mb-4 opacity-30" />
                <p>Nenhum usuário cadastrado ainda.</p>
                <p className="text-sm">Clique em &quot;Adicionar Usuário&quot; ou aguarde cadastros via assinatura.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>WhatsApp</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expiração</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell className="font-medium text-sm">{user.email}</TableCell>
                      <TableCell className="text-sm">{user.name}</TableCell>
                      <TableCell className="text-sm">
                        {user.whatsapp_number ? (
                          <span className="text-green-600 text-xs">{user.whatsapp_number}</span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell>{getPlanBadge(user.plan_type)}</TableCell>
                      <TableCell>{getStatusBadge(user.subscription_status)}</TableCell>
                      <TableCell className="text-sm">
                        {user.expiration_date 
                          ? new Date(user.expiration_date).toLocaleDateString('pt-BR')
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 px-2"
                            onClick={() => handleToggleBlock(user.user_id)}
                            title={user.is_blocked ? 'Desbloquear' : 'Bloquear'}
                          >
                            {user.is_blocked ? <LockOpen size={14} /> : <Lock size={14} />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 px-2"
                            onClick={() => handleResetPassword(user.user_id)}
                            title="Resetar Senha"
                          >
                            <Key size={14} />
                          </Button>
                          {user.whatsapp_number && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7 px-2"
                              onClick={() => handleUnlinkWhatsapp(user.user_id, user.name)}
                              title="Desvincular WhatsApp"
                            >
                              <WhatsappLogo size={14} className="text-red-500" />
                            </Button>
                          )}
                          <Dialog open={editDialogOpen && selectedUser?.user_id === user.user_id} onOpenChange={setEditDialogOpen}>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs h-7 px-2"
                                onClick={() => setSelectedUser({...user, _editedEmail: user.email, _originalEmail: user.email})}
                                title="Editar Usuário"
                              >
                                Editar
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Editar Usuário</DialogTitle>
                                <DialogDescription>{selectedUser?.name}</DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <label className="text-sm font-medium">Email</label>
                                  <Input
                                    type="email"
                                    value={selectedUser?._editedEmail || ''}
                                    onChange={(e) => setSelectedUser({...selectedUser, _editedEmail: e.target.value})}
                                    placeholder="email@exemplo.com"
                                    data-testid="admin-edit-email-input"
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">Corrija o email em caso de erro de digitação</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Status</label>
                                  <Select 
                                    value={selectedUser?.subscription_status || ''} 
                                    onValueChange={(v) => {
                                      const updates = { ...selectedUser, subscription_status: v };
                                      if (v === 'lifetime') {
                                        updates.expiration_date = null;
                                      }
                                      setSelectedUser(updates);
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Selecione o status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="active">Ativo</SelectItem>
                                      <SelectItem value="trial">Trial</SelectItem>
                                      <SelectItem value="lifetime">Vitalício</SelectItem>
                                      <SelectItem value="overdue">Inadimplente</SelectItem>
                                      <SelectItem value="cancelled">Cancelado</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {selectedUser?.subscription_status !== 'lifetime' && (
                                  <div>
                                    <label className="text-sm font-medium">Data de Expiração</label>
                                    <Input 
                                      type="date" 
                                      value={selectedUser?.expiration_date?.split('T')[0] || ''} 
                                      onChange={(e) => setSelectedUser({...selectedUser, expiration_date: e.target.value})}
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">Use para estender vigência manualmente</p>
                                  </div>
                                )}
                                {selectedUser?.subscription_status === 'lifetime' && (
                                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                                    <p className="text-sm text-purple-700 font-medium">Acesso vitalício — sem data de expiração</p>
                                  </div>
                                )}
                                <Button onClick={handleUpdateSubscription} className="w-full" data-testid="admin-save-edit-btn">
                                  Salvar Alterações
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 px-2 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => openDeleteDialog(user)}
                            title="Excluir usuário"
                          >
                            <Trash size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        )}

        {/* Configurações Tab */}
        {activeTab === 'config' && siteConfig && (
          <div className="space-y-4" data-testid="config-dashboard">
            {/* Pixel & Tracking */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  Pixel & Tracking (Tráfego Pago)
                </CardTitle>
                <CardDescription>Configure os pixels para rastrear conversões de campanhas pagas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Meta Pixel ID (Facebook/Instagram)</label>
                  <Input
                    value={siteConfig.meta_pixel_id || ''}
                    onChange={(e) => setSiteConfig({...siteConfig, meta_pixel_id: e.target.value})}
                    placeholder="Ex: 1234567890123456"
                    data-testid="config-meta-pixel-input"
                  />
                  <p className="text-xs text-muted-foreground">Encontre em: Meta Business Suite → Eventos → Pixel</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Google Tag ID (Analytics/Ads)</label>
                  <Input
                    value={siteConfig.google_tag_id || ''}
                    onChange={(e) => setSiteConfig({...siteConfig, google_tag_id: e.target.value})}
                    placeholder="Ex: G-XXXXXXXXXX ou AW-XXXXXXXXXX"
                    data-testid="config-google-tag-input"
                  />
                  <p className="text-xs text-muted-foreground">Encontre em: Google Tag Manager ou Google Analytics</p>
                </div>
                <Button onClick={handleSaveConfig} disabled={savingConfig} data-testid="config-save-pixels-btn">
                  {savingConfig ? 'Salvando...' : 'Salvar Pixels'}
                </Button>
              </CardContent>
            </Card>

            {/* Webhooks */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <LinkSimple size={20} />
                  Webhooks
                </CardTitle>
                <CardDescription>URLs para receber notificações automáticas de pagamento e mensagens</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Mercado Pago (Pagamentos)</label>
                  <div className="flex items-center gap-2">
                    <Input value={siteConfig.webhook_mercadopago || ''} readOnly className="font-mono text-xs bg-muted" />
                    <Button variant="outline" size="sm" onClick={() => handleCopyText(siteConfig.webhook_mercadopago)}>
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">WhatsApp (Meta Cloud API)</label>
                  <div className="flex items-center gap-2">
                    <Input value={siteConfig.webhook_whatsapp || ''} readOnly className="font-mono text-xs bg-muted" />
                    <Button variant="outline" size="sm" onClick={() => handleCopyText(siteConfig.webhook_whatsapp)}>
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* API Keys */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Lock size={20} />
                  Chaves de API
                </CardTitle>
                <CardDescription>Visualize ou edite as chaves de integração. Requer confirmação de senha.</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Default masked view */}
                {!fullKeys && !editingKeys && (
                  <>
                    <div className="space-y-3 mb-4">
                      {[
                        { label: 'Mercado Pago (Public Key)', value: siteConfig.mercadopago_public_key },
                        { label: 'Mercado Pago (Access Token)', value: siteConfig.mercadopago_access_token },
                        { label: 'Meta WhatsApp (Phone ID)', value: siteConfig.meta_phone_number_id },
                        { label: 'Meta WhatsApp (Token)', value: siteConfig.meta_access_token },
                        { label: 'Meta WhatsApp (Verify Token)', value: siteConfig.meta_verify_token },
                        { label: 'Resend (Email)', value: siteConfig.resend_api_key },
                        { label: 'Email Remetente', value: siteConfig.sender_email },
                        { label: 'Emergent LLM Key', value: siteConfig.emergent_llm_key },
                        { label: 'URL do Frontend', value: siteConfig.frontend_url },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between gap-4 py-2 border-b border-muted last:border-0">
                          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{item.label}</span>
                          <span className="text-xs font-mono text-foreground truncate max-w-[300px]">{item.value || '—'}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => requestPasswordVerification('view')} data-testid="config-view-keys-btn">
                        <Eye size={16} className="mr-1" /> Visualizar Completas
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => requestPasswordVerification('edit')} data-testid="config-edit-keys-btn">
                        <Key size={16} className="mr-1" /> Editar Chaves
                      </Button>
                    </div>
                  </>
                )}

                {/* Full keys view (read-only) */}
                {fullKeys && !editingKeys && (
                  <>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <p className="text-xs text-amber-700 font-medium">Exibindo chaves completas. Elas serão ocultadas ao fechar.</p>
                    </div>
                    <div className="space-y-3 mb-4">
                      {[
                        { label: 'Mercado Pago (Public Key)', key: 'mercadopago_public_key' },
                        { label: 'Mercado Pago (Access Token)', key: 'mercadopago_access_token' },
                        { label: 'Meta WhatsApp (Phone ID)', key: 'meta_phone_number_id' },
                        { label: 'Meta WhatsApp (Token)', key: 'meta_access_token' },
                        { label: 'Meta WhatsApp (Verify Token)', key: 'meta_verify_token' },
                        { label: 'Resend (Email)', key: 'resend_api_key' },
                        { label: 'Email Remetente', key: 'sender_email' },
                        { label: 'Emergent LLM Key', key: 'emergent_llm_key' },
                        { label: 'URL do Frontend', key: 'frontend_url' },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-muted last:border-0">
                          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap shrink-0">{item.label}</span>
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="text-xs font-mono text-foreground truncate max-w-[350px]">{fullKeys[item.key] || '—'}</span>
                            {fullKeys[item.key] && (
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={() => handleCopyText(fullKeys[item.key])}>
                                <Copy size={12} />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" size="sm" onClick={handleCloseKeysView}>
                      <EyeSlash size={16} className="mr-1" /> Ocultar Chaves
                    </Button>
                  </>
                )}

                {/* Edit keys mode */}
                {editingKeys && (
                  <>
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                      <p className="text-xs text-red-700 font-medium">Modo de edição. Altere apenas o necessário. Chaves incorretas podem quebrar integrações.</p>
                    </div>
                    <div className="space-y-3 mb-4">
                      {[
                        { label: 'Mercado Pago (Public Key)', key: 'mercadopago_public_key' },
                        { label: 'Mercado Pago (Access Token)', key: 'mercadopago_access_token' },
                        { label: 'Meta WhatsApp (Phone ID)', key: 'meta_phone_number_id' },
                        { label: 'Meta WhatsApp (Token)', key: 'meta_access_token' },
                        { label: 'Meta WhatsApp (Verify Token)', key: 'meta_verify_token' },
                        { label: 'Resend (Email)', key: 'resend_api_key' },
                        { label: 'Email Remetente', key: 'sender_email' },
                        { label: 'Emergent LLM Key', key: 'emergent_llm_key' },
                        { label: 'URL do Frontend', key: 'frontend_url' },
                      ].map((item, i) => (
                        <div key={i} className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">{item.label}</label>
                          <Input
                            value={editingKeys[item.key] || ''}
                            onChange={(e) => setEditingKeys({ ...editingKeys, [item.key]: e.target.value })}
                            className="font-mono text-xs"
                            data-testid={`config-edit-${item.key}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSaveKeys} disabled={savingKeys} data-testid="config-save-keys-btn">
                        {savingKeys ? 'Salvando...' : 'Salvar Chaves'}
                      </Button>
                      <Button variant="outline" onClick={handleCloseKeysView}>
                        Cancelar
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Modal de Confirmação de Senha */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-sm shadow-2xl animate-in fade-in zoom-in-95 duration-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lock size={20} />
                  Confirmação de Segurança
                </CardTitle>
                <CardDescription>
                  {passwordModalAction === 'view' 
                    ? 'Digite sua senha para visualizar as chaves completas' 
                    : 'Digite sua senha para editar as chaves de API'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => { e.preventDefault(); handleVerifyPassword(); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-password-verify">Senha do Administrador</Label>
                    <Input
                      id="admin-password-verify"
                      type="password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      placeholder="Digite sua senha"
                      autoFocus
                      data-testid="config-password-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowPasswordModal(false); setAdminPassword(''); }}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="flex-1" disabled={verifyingPassword || !adminPassword} data-testid="config-password-submit-btn">
                      {verifyingPassword ? 'Verificando...' : 'Confirmar'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Modal de Confirmação de Exclusão */}
        <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            setDeleteStep(1);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-destructive flex items-center gap-2">
                <Warning size={24} weight="fill" />
                {deleteStep === 1 ? 'Confirmar Exclusão' : '⚠️ ÚLTIMA CONFIRMAÇÃO'}
              </DialogTitle>
              <DialogDescription>
                {deleteStep === 1 ? (
                  <span>
                    Deseja realmente excluir o usuário <strong>{userToDelete?.email}</strong>?
                  </span>
                ) : (
                  <div className="space-y-3 mt-2">
                    <p className="text-destructive font-semibold">Esta ação é IRREVERSÍVEL!</p>
                    <p>Todos os dados do usuário serão perdidos:</p>
                    <ul className="list-disc list-inside text-sm">
                      <li>Transações</li>
                      <li>Metas</li>
                      <li>Investimentos</li>
                      <li>Contas</li>
                    </ul>
                    <p className="font-semibold">Tem CERTEZA ABSOLUTA que deseja excluir {userToDelete?.email}?</p>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end mt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setUserToDelete(null);
                  setDeleteStep(1);
                }}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleConfirmDelete}
              >
                {deleteStep === 1 ? 'Sim, Excluir' : 'CONFIRMAR EXCLUSÃO'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Password Result Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setResetDialogOpen(false);
            setResetPasswordResult(null);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key size={20} />
                Senha Resetada
              </DialogTitle>
            </DialogHeader>
            {resetPasswordResult && (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium mb-2">Nova senha temporária:</p>
                  <div className="flex gap-2">
                    <Input
                      value={resetPasswordResult.temp_password}
                      readOnly
                      className="bg-white font-mono"
                      onClick={(e) => e.target.select()}
                    />
                    <Button
                      variant="outline"
                      onClick={async () => {
                        const success = await copyToClipboard(resetPasswordResult.temp_password);
                        if (success) toast.success('Senha copiada!');
                        else toast.error('Copie manualmente.');
                      }}
                    >
                      <Copy size={16} />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">O usuário será obrigado a alterar a senha no próximo login.</p>
                <Button onClick={() => { setResetDialogOpen(false); setResetPasswordResult(null); }} className="w-full">
                  Fechar
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Emails Tab */}
        {activeTab === 'emails' && (
          <div className="space-y-4" data-testid="admin-emails-tab">
            {/* Status counts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'sent', label: 'Enviados', color: 'text-blue-600' },
                { key: 'delivered', label: 'Entregues', color: 'text-green-600' },
                { key: 'failed', label: 'Falharam', color: 'text-red-600' },
                { key: 'pending', label: 'Pendentes', color: 'text-amber-600' },
              ].map(s => (
                <Card key={s.key}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-3xl font-bold ${s.color}`}>{emailCounts[s.key] || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Test email */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Enviar e-mail de teste</CardTitle>
                <CardDescription>Confirme que a entrega está funcionando enviando uma mensagem real.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="destino@exemplo.com"
                    value={emailTestTo}
                    onChange={e => setEmailTestTo(e.target.value)}
                    data-testid="admin-email-test-input"
                  />
                  <Button onClick={handleSendTestEmail} disabled={sendingTestEmail} data-testid="admin-email-test-send">
                    {sendingTestEmail ? 'Enviando...' : 'Enviar teste'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Filter + log */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Histórico de envios</CardTitle>
                  <CardDescription>Últimos 100 e-mails — atualizados em tempo real via webhook do Resend.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={emailStatusFilter} onValueChange={setEmailStatusFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="admin-email-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os status</SelectItem>
                      <SelectItem value="sent">Enviados</SelectItem>
                      <SelectItem value="delivered">Entregues</SelectItem>
                      <SelectItem value="failed">Falharam</SelectItem>
                      <SelectItem value="pending">Pendentes</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={fetchEmailLogs} data-testid="admin-email-refresh">Atualizar</Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Para</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Assunto</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tentativas</TableHead>
                      <TableHead>Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailLogs.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Nenhum e-mail registrado ainda.</TableCell></TableRow>
                    )}
                    {emailLogs.map(log => {
                      const statusBadge = {
                        sent: 'bg-blue-100 text-blue-700',
                        delivered: 'bg-green-100 text-green-700',
                        failed: 'bg-red-100 text-red-700',
                        pending: 'bg-amber-100 text-amber-700',
                      }[log.status] || 'bg-gray-100 text-gray-700';
                      const date = new Date(log.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                      const canResend = ['welcome', 'password_reset'].includes(log.tag) && log.status !== 'delivered';
                      return (
                        <TableRow key={log.log_id} data-testid={`email-log-${log.log_id}`}>
                          <TableCell className="text-xs">{date}</TableCell>
                          <TableCell className="text-xs font-mono">{log.to}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{log.tag}</Badge></TableCell>
                          <TableCell className="text-xs max-w-[240px] truncate">{log.subject}</TableCell>
                          <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge}`}>{log.status}</span></TableCell>
                          <TableCell className="text-xs text-center">{log.attempts}</TableCell>
                          <TableCell>
                            {canResend && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={resendingId === log.log_id}
                                onClick={() => handleResendEmail(log.log_id, log.tag)}
                                data-testid={`resend-${log.log_id}`}
                              >
                                {resendingId === log.log_id ? '...' : 'Reenviar'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Best-practices note */}
            <Card className="bg-blue-50/50 border-blue-200">
              <CardContent className="p-4 text-sm text-blue-900 space-y-2">
                <p className="font-semibold">📨 Boas práticas de entregabilidade</p>
                <p>• Configure <strong>SPF</strong>, <strong>DKIM</strong> e <strong>DMARC</strong> no DNS do <code>aretesystem.com.br</code>. O Resend fornece os registros em <em>Domains → DNS</em>.</p>
                <p>• Configure o webhook do Resend para <code>https://aretesystem.com.br/api/webhook/resend</code> (eventos: <em>email.delivered</em>, <em>email.bounced</em>, <em>email.complained</em>).</p>
                <p>• Se um cliente reclamar que não recebeu, use o botão &quot;Reenviar&quot; — ele gera nova senha e dispara novo envio.</p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
